import { useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import styles from './index.module.scss'
import { forwardRef } from "react"
import raf from "rc-util/es/raf";
import { getClientSize } from 'rc-util/lib/Dom/css';
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import * as _ from "underscore";
import useResizeObserver from "@utils/useResizeObserver"
import { VideoContext } from "../../hooks/VideoContext";

function fixPoint(key, start, width, clientWidth) {
    const startAddWidth = start + width;
    const offsetStart = (width - clientWidth) / 2;
  
    if (width > clientWidth) {
      if (start > 0) {
        return {
          [key]: offsetStart,
        };
      }
      if (start < 0 && startAddWidth < clientWidth) {
        return {
          [key]: -offsetStart,
        };
      }
    } else if (start < 0 || startAddWidth > clientWidth) {
      return {
        [key]: start < 0 ? offsetStart : -offsetStart,
      };
    }
    return {};
  }
  
  /**
   * Fix positon x,y point when
   *
   * Ele width && height < client
   * - Back origin
   *
   * - Ele width | height > clientWidth | clientHeight
   * - left | top > 0 -> Back 0
   * - left | top + width | height < clientWidth | clientHeight -> Back left | top + width | height === clientWidth | clientHeight
   *
   * Regardless of other
   */
function getFixScaleEleTransPosition(
    width,
    height,
    left,
    top,
    parentWidth,
    parentHeight
) {
    let fixPos = null;

    if (width <= parentWidth && height <= parentHeight) {
        fixPos = {
        x: 0,
        y: 0,
        };
    } else if (width > parentWidth || height > parentHeight) {
        fixPos = {
        ...fixPoint('x', left, width, parentWidth),
        ...fixPoint('y', top, height, parentHeight),
        };
    }

    return fixPos;
}

const ImageRender = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        getBoundingClientRect: () => {
            return imgRef.current.getBoundingClientRect()
        },
        onWheel,
        onMouseMove,
        onMouseUp,
    }))

    const [transform, setTransform] = useState({x: 0, y: 0, scale: 1})
    const minScale = 0.5
    const maxScale = 100

    useEffect(() => {
        props.onTransform &&
        props.onTransform(transform)
    }, [])

    const style = useMemo(() => {
        return {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0px) scale3d(${transform.scale}, ${transform.scale}, 1) rotate(0deg)`,
            userSelect: "none",
        }
    }, [transform])

    const imgRef = useResizeObserver((entries) => {
        for (let entry of entries) {
            // 处理尺寸变化的逻辑
            props.onLoad &&
            props.onLoad({
                clientWidth: entry.contentRect.width,
                clientHeight: entry.contentRect.height
            })
        }
    })
    const frame = useRef(null);
    const queue = useRef([]);
    /** Direct update transform */
    var updateTransform = function updateTransform(newTransform) {
        if (frame.current === null) {
            queue.current = [];
            frame.current = raf(function () {
                setTransform(function (preState) {
                    var memoState = preState;
                    queue.current.forEach(function (queueState) {
                        memoState = _objectSpread(_objectSpread({}, memoState), queueState);
                    });
                    frame.current = null;
                    props.onTransform === null || props.onTransform === void 0 || props.onTransform(memoState);
                    return memoState;
                });
            });
        }
        queue.current.push(_objectSpread(_objectSpread({}, transform), newTransform));
    };
    const dispatchZoomChange = (ratio, centerX, centerY) => {
        const _imgRef$current = imgRef.current,
            offsetWidth = _imgRef$current.offsetWidth,
            offsetHeight = _imgRef$current.offsetHeight,
            offsetLeft = _imgRef$current.offsetLeft,
            offsetTop = _imgRef$current.offsetTop;

        let newRatio = ratio;
        let newScale = transform.scale * ratio;
        if (newScale > maxScale) {
            newScale = maxScale;
            newRatio = maxScale /  transform.scale;
        } else if (newScale < minScale) {
            // For mobile interactions, allow scaling down to the minimum scale.
            newScale = minScale;
            newRatio = newScale / transform.scale;
        }

        /** Default center point scaling */
        var mergedCenterX = centerX
        var mergedCenterY = centerY
        var diffRatio = newRatio - 1;
        /** Deviation calculated from image size */
        var diffImgX = diffRatio * offsetWidth * 0.5;
        var diffImgY = diffRatio * offsetHeight * 0.5;
        /** The difference between the click position and the edge of the document */
        var diffOffsetLeft = diffRatio * (mergedCenterX - transform.x - offsetLeft);
        var diffOffsetTop = diffRatio * (mergedCenterY - transform.y - offsetTop);
        /** Final positioning */
        let newX = transform.x - (diffOffsetLeft - diffImgX);
        let newY = transform.y - (diffOffsetTop - diffImgY);

        /**
         * When zooming the image
         * When the image size is smaller than the width and height of the window, the position is initialized
         */
        if (ratio < 1 && newScale === 1) {
            const mergedWidth = offsetWidth * newScale;
            const mergedHeight = offsetHeight * newScale;
            const clientWidth = document.documentElement.clientWidth
            const clientHeight = document.documentElement.clientHeight;
            if (mergedWidth <= clientWidth && mergedHeight <= clientHeight) {
                newX = 0;
                newY = 0;
            }
        }
        updateTransform({x: newX, y: newY, scale: newScale})
    }

    const onWheel = (e) => {
        if (e.deltaY == 0) return
        const scaleStep = 0.2
        // Scale ratio depends on the deltaY size
        const scaleRatio = Math.abs(e.deltaY / 100)
        // Limit the maximum scale ratio
        const mergedScaleRatio = Math.min(scaleRatio, 1)
        // Scale the ratio each time
        let ratio = 1 + mergedScaleRatio * scaleStep
        if (e.deltaY > 0) {
            ratio = 1 / ratio
        }
        const boundRect = imgRef.current.parentNode.getBoundingClientRect()
        dispatchZoomChange(ratio, e.clientX - boundRect.left, e.clientY)
    }

    const [isMoving, setMoving] = useState(false);
    const startPositionInfo = useRef({
        diffX: 0,
        diffY: 0,
        transformX: 0,
        transformY: 0,
    });
    const onMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        startPositionInfo.current = {
            diffX: e.pageX - transform.x,
            diffY: e.pageY - transform.y,
            transformX: transform.x,
            transformY: transform.y,
        };
        setMoving(true);
    }
    const onMouseMove = (e) => {
        if (isMoving) {
            updateTransform({
                x: e.pageX - startPositionInfo.current.diffX,
                y: e.pageY - startPositionInfo.current.diffY,
            })
        }
    }
    const onMouseUp = (e) => {
        if (isMoving) {
            setMoving(false)
            /** No need to restore the position when the picture is not moved, So as not to interfere with the click */
            const { transformX, transformY } = startPositionInfo.current;
            const hasChangedPosition = transform.x !== transformX && transform.y !== transformY;
            if (!hasChangedPosition) return;

            const parentNode = imgRef.current.offsetParent

            const width = imgRef.current.offsetWidth * transform.scale;
            const height = imgRef.current.offsetHeight * transform.scale;

            const parentWidth = parentNode.offsetWidth
            const parentHeight = parentNode.offsetHeight


            const { left, top } = imgRef.current.getBoundingClientRect();
            const { parentLeft, parentTop } = parentNode.getBoundingClientRect();

            const fixState = getFixScaleEleTransPosition(
                width,
                height,
                left - parentLeft,
                top - parentTop,
                parentWidth,
                parentHeight
            );

            if (fixState) {
                updateTransform({ ...fixState });
            }
        }
    }
    const handleLoad = (e) => {
        props.onLoad &&
        props.onLoad(e.target)
    }

    // 视频播放相关
    const {
        videoRef,
        setIsLoading,
        setIsPlaying,
        setCurrentTime,
        setDuration,
    } = useContext(VideoContext)
    const isVideo = useMemo(() => {
        return /\.mp4/.test(props.src)
    }, [props.src])
    useEffect(() => {
        if (!isVideo) return
        videoRef.current = imgRef.current
        const video = imgRef.current
        const handleTimeUpdate = () => {
            setCurrentTime(video.currentTime)
        }
        const handlePlay = () => {
            setIsPlaying(true)
            setIsLoading(false);
        }
        const handlePause = () => {
            setIsPlaying(false)
        }
        const handleLoadedMetadata = () => {
            setDuration(video.duration)
        }
        const handleWaiting = () => {
            setIsLoading(true)
        }
        const handleCanPlay = () => {
            setIsLoading(false)
        }
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };
        // 添加事件监听器
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('ended', handleEnded);
        // 清理函数
        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('waiting', handleWaiting);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('ended', handleEnded);
        }
    }, [isVideo])

    return (
        isVideo ?
        <video
            ref={imgRef}
            className={styles.ImageRender}
            style={style}
            src={props.src}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onLoad={handleLoad}
            autoPlay
            loop
        /> : <img
            ref={imgRef}
            className={styles.ImageRender}
            style={style}
            src={props.src}
            onWheel={onWheel}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onLoad={handleLoad}
        />
    )
})

export default ImageRender